import { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  MoreHorizontal,
  Eye,
  BadgeCheck,
  Crown,
  Ban,
  ShieldAlert,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner, EmptyState, PremiumBadge } from "@/components/shared";
import { PromptDialog, ConfirmDialog } from "@/components/dialogs";
import { SubscriptionDialog } from "@/components/SubscriptionDialog";
import { UserDetailSheet } from "@/components/UserDetailSheet";

const STATUS = [
  { value: "all", label: "All members" },
  { value: "verified", label: "Verified" },
  { value: "unverified", label: "Unverified" },
  { value: "premium", label: "Premium" },
  { value: "banned", label: "Banned" },
  { value: "reported", label: "Reported" },
];
const LIMIT = 25;

export default function Users() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState("all");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [detailUid, setDetailUid] = useState(null);
  const [dialog, setDialog] = useState(null); // {type, user}
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setSkip(0);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.users({
        q: debouncedQ,
        status: status === "all" ? "" : status,
        skip,
        limit: LIMIT,
      });
      setData(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, status, skip]);

  useEffect(() => {
    load();
  }, [load]);

  // Clear selection only when the query/filter/page actually changes.
  useEffect(() => {
    setSelected(new Set());
  }, [debouncedQ, status, skip]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleOne = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const allOnPage = data.users.length > 0 && data.users.every((u) => selected.has(u.user_id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (data.users.every((u) => prev.has(u.user_id))) return new Set();
      return new Set(data.users.map((u) => u.user_id));
    });
  };

  const runBulk = async (fn, verb) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => fn(id)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok) toast.success(`${verb} ${ok} user${ok === 1 ? "" : "s"}`);
      if (fail) toast.error(`${fail} action${fail === 1 ? "" : "s"} failed`);
      setSelected(new Set());
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = () => {
    const rows = data.users || [];
    if (!rows.length) {
      toast.error("Nothing to export");
      return;
    }
    const cols = [
      "user_id", "name", "email", "city", "country", "age",
      "verified", "premium", "banned", "role", "reports", "warnings", "created_at",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      cols.join(","),
      ...rows.map((u) => cols.map((c) => esc(u[c])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `amoreska-users-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast.success(`Exported ${rows.length} users`);
  };

  const total = data.total || 0;
  const page = Math.floor(skip / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle={`${total} members`}
        actions={
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9"
            data-testid="users-search"
          />
        </div>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            setSkip(0);
          }}
        >
          <SelectTrigger className="w-full sm:w-52" data-testid="users-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`status-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 mb-4 rounded-xl bg-primary/15 ring-1 ring-primary/40 px-4 py-3 flex-wrap"
          data-testid="bulk-bar"
        >
          <span className="text-sm font-medium" data-testid="bulk-count">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={bulkBusy}
              onClick={() => runBulk((id) => api.verify(id, true), "Verified")}
              data-testid="bulk-verify"
            >
              <BadgeCheck className="h-4 w-4" /> Verify all
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={bulkBusy}
              onClick={() => setDialog({ type: "bulk-ban" })}
              data-testid="bulk-ban"
            >
              <Ban className="h-4 w-4" /> Ban all
            </Button>
            <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={() => setSelected(new Set())} data-testid="bulk-clear">
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-card ring-1 ring-border/70 overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : data.users.length === 0 ? (
          <EmptyState icon={UsersIcon} title="No users found" hint="Try adjusting your search or filters." />
        ) : (
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allOnPage}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    data-testid="select-all"
                  />
                </TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Reports</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((u) => (
                <TableRow
                  key={u.user_id}
                  className="border-border/50 cursor-pointer"
                  onClick={() => setDetailUid(u.user_id)}
                  data-testid={`user-row-${u.user_id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(u.user_id)}
                      onCheckedChange={() => toggleOne(u.user_id)}
                      aria-label={`Select ${u.name}`}
                      data-testid={`select-${u.user_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar src={u.photo} name={u.name} size={40} />
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          {u.name || "Unnamed"}
                          {u.age ? <span className="text-muted-foreground font-normal">· {u.age}</span> : null}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {[u.city, u.country].filter(Boolean).join(", ") || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {u.verified && <Tag tone="green"><BadgeCheck className="h-3 w-3" />Verified</Tag>}
                      <PremiumBadge user={u} />
                      {u.banned && <Tag tone="red"><Ban className="h-3 w-3" />Banned</Tag>}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {u.reports > 0 ? (
                      <Tag tone="red">{u.reports}</Tag>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <RowMenu
                      user={u}
                      onView={() => setDetailUid(u.user_id)}
                      onVerify={() =>
                        act(() => api.verify(u.user_id, !u.verified), u.verified ? "Verification revoked" : "User verified")
                      }
                      onSubscription={() => setDialog({ type: "subscription", user: u })}
                      onWarn={() => setDialog({ type: "warn", user: u })}
                      onBan={() => setDialog({ type: "ban", user: u })}
                      onUnban={() => act(() => api.unban(u.user_id), "User unbanned")}
                      onDelete={() => setDialog({ type: "delete", user: u })}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">
            Page {page} of {pages} · {total} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - LIMIT))}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages}
              onClick={() => setSkip(skip + LIMIT)}
              data-testid="next-page"
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <UserDetailSheet
        uid={detailUid}
        open={!!detailUid}
        onOpenChange={(v) => !v && setDetailUid(null)}
        onChanged={load}
      />

      <PromptDialog
        open={dialog?.type === "ban"}
        onOpenChange={(v) => !v && setDialog(null)}
        title={`Ban ${dialog?.user?.name || "user"}`}
        description="They will immediately lose access to Amoreska."
        confirmText="Ban user"
        destructive
        onConfirm={(reason) => act(() => api.ban(dialog.user.user_id, reason), "User banned")}
      />
      <PromptDialog
        open={dialog?.type === "warn"}
        onOpenChange={(v) => !v && setDialog(null)}
        title={`Warn ${dialog?.user?.name || "user"}`}
        description="Issue a formal warning to this member."
        confirmText="Send warning"
        onConfirm={(reason) => act(() => api.warn(dialog.user.user_id, reason), "Warning issued")}
      />
      <PromptDialog
        open={dialog?.type === "bulk-ban"}
        onOpenChange={(v) => !v && setDialog(null)}
        title={`Ban ${selected.size} selected user${selected.size === 1 ? "" : "s"}`}
        description="Every selected member will immediately lose access to Amoreska. The same reason applies to all."
        confirmText={`Ban ${selected.size} user${selected.size === 1 ? "" : "s"}`}
        destructive
        onConfirm={(reason) => runBulk((id) => api.ban(id, reason), "Banned")}
      />
      <ConfirmDialog
        open={dialog?.type === "delete"}
        onOpenChange={(v) => !v && setDialog(null)}
        title={`Delete ${dialog?.user?.name || "user"}?`}
        description="This permanently removes the user and cannot be undone."
        confirmText="Delete permanently"
        destructive
        onConfirm={() => act(() => api.deleteUser(dialog.user.user_id), "User deleted")}
      />
      <SubscriptionDialog
        open={dialog?.type === "subscription"}
        onOpenChange={(v) => !v && setDialog(null)}
        user={dialog?.type === "subscription" ? dialog.user : null}
        onDone={load}
      />
    </div>
  );
}

function RowMenu({ user, onView, onVerify, onSubscription, onWarn, onBan, onUnban, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-menu-${user.user_id}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={onView} data-testid="action-view">
          <Eye className="h-4 w-4" /> View detail
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onVerify} data-testid="action-verify">
          <BadgeCheck className="h-4 w-4" /> {user.verified ? "Unverify" : "Verify"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSubscription} data-testid="action-subscription">
          <Crown className="h-4 w-4" /> Manage subscription
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onWarn} data-testid="action-warn">
          <ShieldAlert className="h-4 w-4" /> Warn
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {user.banned ? (
          <DropdownMenuItem onClick={onUnban} data-testid="action-unban">
            <Ban className="h-4 w-4" /> Unban
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={onBan} className="text-red-300 focus:text-red-200" data-testid="action-ban">
            <Ban className="h-4 w-4" /> Ban
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onDelete} className="text-red-300 focus:text-red-200" data-testid="action-delete">
          <Trash2 className="h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
